/**
 * DOM extraction utilities for drag-and-drop intent calculation
 * Isolates all DOM manipulation from business logic
 */

/**
 * Metadata extracted from DOM about the target node (what the dragged item is being dropped onto).
 *
 * Used by calculateIntentPure to determine drag intent without touching DOM.
 */
export type TargetMeta = {
  /** ID of the target node element (where the dragged item is being dropped) */
  targetId: string;

  /** Type of the target node (CATEGORY or ITEM) */
  targetType: "ITEM" | "CATEGORY";

  /** Parent ID of the target node (null if root level) */
  targetParentId: string | null;

  /** Bounding rectangle of the target node (used for before/after position calculation) */
  targetRect: DOMRect;

  /** Next sibling ID of the target node (if any) */
  targetNextNodeId: string | null;

  /** Previous sibling ID of the target node (if any) */
  targetPrevNodeId: string | null;
};

/**
 * Parses and validates node type from DOM attribute
 * Returns null if invalid
 */
export function parseNodeType(
  value: string | null
): "ITEM" | "CATEGORY" | null {
  if (!value) return null;
  if (value === "ITEM" || value === "CATEGORY") {
    return value;
  }
  return null;
}

/**
 * Parses parent ID from DOM attribute
 * Handles "null" string → null conversion
 */
export function parseParentId(value: string | null): string | null {
  if (!value || value === "null") return null;
  return value;
}

/**
 * Extracts target node metadata from DOM at the given mouse position.
 *
 * PURPOSE: Transform raw DOM state into structured data for drag-and-drop intent calculation.
 * This function does ONLY DOM extraction - no business logic.
 *
 * @returns TargetMeta object containing:
 *   - targetId: The ID of the target node element (where dragged item will be dropped)
 *   - targetType: Type of target node (CATEGORY or ITEM)
 *   - targetParentId: Parent ID of target node
 *   - targetRect: Bounding rectangle (for position calculations like before/after)
 *
 * Returns null if no valid node found at the point.
 */
export function getTargetMetaAtPoint(x: number, y: number): TargetMeta | null {
  // Step 1: Find the element at mouse position
  const element = document.elementFromPoint(x, y);
  if (!element) {
    return null;
  }

  // Step 2: Find the closest node element (one with data-node-id attribute)
  const nodeElement = element.closest("[data-node-id]");
  if (!nodeElement) {
    return null;
  }

  // Step 3: Extract and validate node attributes
  const nodeId = nodeElement.getAttribute("data-node-id");
  const nodeType = parseNodeType(nodeElement.getAttribute("data-node-type"));
  const nodeParentId = parseParentId(
    nodeElement.getAttribute("data-node-parent-id")
  );
  const nextNodeId = nodeElement.getAttribute("data-node-next-item-id");
  const prevNodeId = nodeElement.getAttribute("data-node-prev-item-id");

  if (!nodeId || !nodeType) {
    return null;
  }

  // Step 4: Get element's position info (needed for before/after calculation)
  const rect = nodeElement.getBoundingClientRect();

  return {
    targetId: nodeId,
    targetType: nodeType,
    targetParentId: nodeParentId,
    targetNextNodeId: nextNodeId,
    targetPrevNodeId: prevNodeId,
    targetRect: rect,
  };
}
